import React from "react";
import { api, formatPHP } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import * as Phosphor from "@phosphor-icons/react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const ICON_OPTIONS = [
  "Sparkle", "ForkKnife", "Camera", "Music", "FlowerLotus", "Heart",
  "Confetti", "Cake", "MaskHappy", "Boat", "Mountains", "Fire",
  "Coffee", "Bathtub", "Wind", "Star", "Crown", "GiftIcon",
];

const initial = () => ({
  name: "",
  description: "",
  price: 0,
  price_note: "",
  icon: "Sparkle",
  order: 0,
  active: true,
  is_addon: false,
  addon_pricing: "flat",
});

const ADDON_PRICING_LABELS = {
  flat: "flat / per booking",
  per_day: "per day of stay",
  per_guest: "per guest",
};

export default function AdminServices() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(initial());
  const [editingId, setEditingId] = React.useState(null);

  const load = () => api.get("/services").then((r) => setItems(r.data || []));
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Service name is required");
    try {
      const payload = { ...form, price: Number(form.price) || 0, order: Number(form.order) || 0 };
      if (editingId) {
        await api.put(`/services/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/services", payload);
        toast.success("Added");
      }
      setForm(initial());
      setEditingId(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const edit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      description: s.description || "",
      price: s.price || 0,
      price_note: s.price_note || "",
      icon: s.icon || "Sparkle",
      order: s.order || 0,
      active: s.active !== false,
      is_addon: !!s.is_addon,
      addon_pricing: s.addon_pricing || "flat",
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`);
    toast.success("Removed");
    load();
  };

  const PreviewIcon = Phosphor[form.icon] || Phosphor.Sparkle;

  return (
    <div data-testid="admin-services-page">
      <div className="mb-8">
        <p className="overline mb-2">Curated Add-ons</p>
        <h1 className="font-serif-display text-4xl">Other Services</h1>
        <p className="text-[#A1A4B5] text-sm mt-2 max-w-2xl">
          Add extras you offer guests — catering, massage, photography, fireworks,
          karaoke rental, etc. Set a price (₱) or leave it at 0 to display "Inquire".
        </p>
      </div>

      <form
        onSubmit={save}
        className="border border-[var(--border)] rounded-2xl p-6 mb-10 bg-[#13151D]"
        data-testid="service-form"
      >
        <h3 className="font-serif-display text-xl mb-4">
          {editingId ? "Edit Service" : "Add Service"}
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Name</Label>
            <Input
              value={form.name}
              required
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="service-name-input"
              placeholder="e.g., In-villa Massage"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Icon</Label>
            <div className="flex gap-2 mt-2 items-center">
              <select
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="flex-1 bg-[#0A0B10] border border-[var(--border)] rounded-md py-2 px-3 text-sm"
                data-testid="service-icon-select"
              >
                {ICON_OPTIONS.map((i) => (<option key={i} value={i}>{i}</option>))}
              </select>
              <span className="w-10 h-10 rounded border border-[var(--border)] grid place-items-center text-[#C5A059]">
                <PreviewIcon size={20} weight="thin" />
              </span>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              rows={2}
              data-testid="service-description-input"
              placeholder="Short description for guests…"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
              Price (₱) <span className="normal-case tracking-normal text-[#A1A4B5]/70">— 0 = Inquire</span>
            </Label>
            <Input
              type="number"
              min="0"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="service-price-input"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Price Note</Label>
            <Input
              value={form.price_note}
              onChange={(e) => setForm({ ...form, price_note: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="service-price-note-input"
              placeholder="e.g., per pax · From · per day"
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Order</Label>
            <Input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="service-order-input"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-[#A1A4B5] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="accent-[#C5A059]"
                data-testid="service-active-input"
              />
              Active (visible on public site)
            </label>
          </div>
          <div className="md:col-span-2 border-t border-[var(--border)] pt-4 grid md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-xs text-[#A1A4B5] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_addon}
                  onChange={(e) => setForm({ ...form, is_addon: e.target.checked })}
                  className="accent-[#C5A059]"
                  data-testid="service-is-addon-input"
                />
                <span>
                  <span className="text-white">Bookable add-on</span> — guests can select this during
                  booking and the price is added to their total (paid at the villa)
                </span>
              </label>
            </div>
            {form.is_addon && (
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Add-on Pricing</Label>
                <select
                  value={form.addon_pricing}
                  onChange={(e) => setForm({ ...form, addon_pricing: e.target.value })}
                  className="mt-2 w-full bg-[#0A0B10] border border-[var(--border)] rounded-md py-2 px-3 text-sm"
                  data-testid="service-addon-pricing-select"
                >
                  <option value="flat">Flat — once per booking</option>
                  <option value="per_day">Per day — price × days of stay</option>
                  <option value="per_guest">Per guest — price × number of guests</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button type="submit" className="gold-btn rounded-full px-6" data-testid="service-save-button">
            <Plus size={14} className="mr-2" />
            {editingId ? "Update" : "Add"}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setEditingId(null); setForm(initial()); }}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((s) => {
          const Icon = Phosphor[s.icon] || Phosphor.Sparkle;
          const priceDisplay =
            Number(s.price) > 0
              ? `${(s.price_note || "").trim()} ${formatPHP(s.price)}`.trim()
              : "Inquire";
          return (
            <div
              key={s.id}
              className={`border rounded-xl p-5 flex items-start gap-4 ${
                s.active ? "border-[var(--border)] bg-[#13151D]" : "border-[var(--border)] bg-[#13151D]/50 opacity-60"
              }`}
              data-testid={`service-item-${s.id}`}
            >
              <div className="text-[#C5A059] mt-1">
                <Icon size={24} weight="thin" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-serif-display text-lg">{s.name}</div>
                  {!s.active && (
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[#A1A4B5] border border-[var(--border)] rounded px-1.5 py-0.5">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#A1A4B5] mt-1">{s.description}</div>
                <div className="text-sm text-[#C5A059] font-medium mt-2">
                  {priceDisplay}
                </div>
                {s.is_addon && (
                  <div className="mt-2 inline-block text-[9px] uppercase tracking-[0.16em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-1.5 py-0.5 rounded" data-testid={`service-addon-badge-${s.id}`}>
                    Bookable add-on · {ADDON_PRICING_LABELS[s.addon_pricing || "flat"]}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="bg-transparent border-[var(--border)]" onClick={() => edit(s)} data-testid={`edit-service-${s.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-[#A1A4B5] text-sm col-span-full">
            No services yet. Add your first one above.
          </p>
        )}
      </div>
    </div>
  );
}
